import React from 'react';
import '@fortawesome/fontawesome-free/css/all.min.css';
import contactBanner from '../../assets/images/banners/contact_team.png';
import salientIso from '../../assets/images/logos/salient-isotipo-blanco.png';
import FaqItem from '../Helpers/FaqItem';
import { useLanguage } from '../../i18n/LanguageContext';

// Brillo que sigue al cursor (efecto spotlight estilo Framer/Linear)
const handleMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--x', `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty('--y', `${e.clientY - r.top}px`);
};

// Tokens de estilo compartidos de las tarjetas de contacto (marca).
const cardBase = 'group relative overflow-hidden rounded-2xl bg-white border border-slate-200/80 shadow-sm p-8 pl-9 flex flex-col transition-all duration-300 ease-out hover:-translate-y-2 hover:shadow-2xl hover:border-salient-green/50 will-change-transform';
const iconBadge = 'w-16 h-16 mb-6 flex items-center justify-center rounded-full bg-gradient-to-br from-salient-primary to-salient-primary-hover text-white text-2xl ring-4 ring-salient-green/15 border border-salient-green/30 transition-transform duration-300 ease-out group-hover:scale-110 group-hover:-rotate-3';
const accentBar = 'absolute left-0 top-0 h-full w-1.5 bg-gradient-to-b from-salient-green to-salient-primary';
const spotlight = { background: 'radial-gradient(380px circle at var(--x) var(--y), rgba(153,225,122,0.18), transparent 60%)' };

const Contact = () => {
    const { t } = useLanguage();
    const faqs = t('contact.faqs');
    return (
        <>
            {/* Hero */}
            <section className="bg-navy-gradient relative overflow-hidden pt-32 pb-20 min-h-[34rem] flex items-center">
                {/* Foto difuminada hacia el fondo (derecha) */}
                <img
                    src={contactBanner}
                    alt="Salient"
                    className="pointer-events-none select-none absolute top-[1.5rem] bottom-0 right-0 w-full sm:w-[65%] lg:w-[52%] object-cover"
                    style={{
                        objectPosition: 'center center',
                        maskImage: 'linear-gradient(to right, transparent 0%, #000 38%)',
                        WebkitMaskImage: 'linear-gradient(to right, transparent 0%, #000 38%)',
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-salient-navy via-salient-navy/85 to-transparent"></div>
                <div className="absolute -bottom-40 -left-32 w-[28rem] h-[28rem] bg-salient-primary/15 rounded-full blur-3xl"></div>
                <div
                    className="dot-grid pointer-events-none absolute top-24 left-6 w-32 h-24 opacity-40"
                    style={{ maskImage: 'linear-gradient(135deg,#000,transparent 75%)', WebkitMaskImage: 'linear-gradient(135deg,#000,transparent 75%)' }}
                ></div>

                <div className="contain relative z-10">
                    <div className="max-w-xl" data-aos="fade-right">
                        <span className="inline-block px-4 py-1 rounded-full bg-salient-green/20 border border-salient-green/40 text-salient-green text-sm font-semibold mb-6">
                            {t('contact.badge')}
                        </span>
                        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{t('contact.title')}</h1>
                        <span className="title-underline mb-6"></span>
                        <p className="text-slate-200 text-lg leading-relaxed">
                            {t('contact.intro1')}
                        </p>
                        <p className="text-slate-300 mt-4">
                            {t('contact.intro2')}
                        </p>
                    </div>
                </div>
            </section>

            {/* Información de contacto — fondo claro, lenguaje visual alineado al inicio */}
            <section
                className="relative overflow-hidden py-24"
                style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #eef3f8 100%)' }}
            >
                <div
                    className="dot-grid pointer-events-none absolute top-10 right-8 w-44 h-32 opacity-60 hidden md:block"
                    style={{ maskImage: 'linear-gradient(225deg, #000, transparent 75%)', WebkitMaskImage: 'linear-gradient(225deg, #000, transparent 75%)' }}
                ></div>
                <div className="contain relative z-10">
                    <div className="text-center max-w-2xl mx-auto mb-14" data-aos="fade-up">
                        <h2 className="text-salient-primary font-bold text-4xl mb-4">{t('contact.infoTitle')}</h2>
                        <span className="title-underline mx-auto"></span>
                        <p className="text-gray-600 mt-4">{t('contact.infoSubtitle')}</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto items-stretch">
                        {/* Correo */}
                        <div onMouseMove={handleMove} className={cardBase} data-aos="fade-up">
                            <span className={accentBar}></span>
                            <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={spotlight}></div>
                            <div className="relative z-10 flex flex-col h-full">
                                <div className={iconBadge}><i className="fa fa-envelope"></i></div>
                                <h3 className="font-heading text-xl font-bold text-salient-primary mb-2">{t('contact.emailTitle')}</h3>
                                <span className="block w-10 h-1 rounded-full bg-salient-green mb-4"></span>
                                <a href="mailto:ventas@salient.mx" className="text-slate-700 hover:text-salient-primary font-medium break-all">ventas@salient.mx</a>
                                <p className="text-sm text-slate-500 mt-2">{t('contact.emailNote')}</p>
                            </div>
                        </div>

                        {/* WhatsApp */}
                        <div onMouseMove={handleMove} className={cardBase} data-aos="fade-up" data-aos-delay="100">
                            <span className={accentBar}></span>
                            <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={spotlight}></div>
                            <div className="relative z-10 flex flex-col h-full">
                                <div className={iconBadge}><i className="fab fa-whatsapp"></i></div>
                                <h3 className="font-heading text-xl font-bold text-salient-primary mb-2">{t('contact.whatsappTitle')}</h3>
                                <span className="block w-10 h-1 rounded-full bg-salient-green mb-4"></span>
                                <a href="https://wa.me/524423424429?text=Hola,%20me%20gustaría%20recibir%20información%20sobre%20sus%20servicios." target="_blank" rel="noreferrer" className="text-slate-700 hover:text-salient-primary font-medium">+52 442 342 4429</a>
                                <p className="text-sm text-slate-500 mt-2">{t('contact.whatsappNote')}</p>
                            </div>
                        </div>

                        {/* Síguenos */}
                        <div onMouseMove={handleMove} className={cardBase} data-aos="fade-up" data-aos-delay="200">
                            <span className={accentBar}></span>
                            <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={spotlight}></div>
                            <div className="relative z-10 flex flex-col h-full">
                                <div className={iconBadge}><i className="fa fa-share-nodes"></i></div>
                                <h3 className="font-heading text-xl font-bold text-salient-primary mb-2">{t('contact.followTitle')}</h3>
                                <span className="block w-10 h-1 rounded-full bg-salient-green mb-4"></span>
                                <p className="text-sm text-slate-500 mb-4">{t('contact.followNote')}</p>
                                <div className="flex gap-3 mt-auto">
                                    <a href="https://www.linkedin.com/company/109912525/" target="_blank" rel="noreferrer" aria-label="LinkedIn" className="w-11 h-11 rounded-full bg-salient-primary flex items-center justify-center text-white hover:bg-salient-primary-hover transition-colors">
                                        <i className="fab fa-linkedin-in"></i>
                                    </a>
                                    <a href="https://www.facebook.com/salientmkt/" target="_blank" rel="noreferrer" aria-label="Facebook" className="w-11 h-11 rounded-full bg-salient-primary flex items-center justify-center text-white hover:bg-salient-primary-hover transition-colors">
                                        <i className="fab fa-facebook-f"></i>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="bg-white py-24">
                <div className="contain">
                    <div className="text-center mb-12" data-aos="fade-up">
                        <h2 className="text-3xl md:text-4xl font-bold text-salient-primary mb-4">{t('contact.faqTitle')}</h2>
                        <span className="title-underline mx-auto"></span>
                    </div>

                    <div className="max-w-4xl mx-auto space-y-4">
                        {faqs.map((faq, i) => (
                            <FaqItem key={i} q={faq.q} a={faq.a} />
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-navy-gradient relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
                <img
                    src={salientIso}
                    alt=""
                    aria-hidden="true"
                    className="pointer-events-none select-none absolute right-0 top-1/2 -translate-y-1/2 h-[160%] w-auto opacity-[0.05] hidden md:block"
                />
                <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-salient-green/10 blur-3xl z-0"></div>

                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <div className="flex justify-center mb-6">
                        <div className="w-16 h-16 rounded-full flex items-center justify-center bg-salient-green/15 border border-salient-green/40">
                            <i className="fa fa-rocket text-salient-green text-2xl"></i>
                        </div>
                    </div>
                    <h2 className="text-4xl sm:text-5xl font-bold text-white mb-2">{t('contact.ctaTitle')}</h2>
                    <span className="title-underline mx-auto my-6"></span>
                    <p className="mt-4 text-xl sm:text-2xl text-slate-300 leading-relaxed font-light">
                        {t('contact.ctaBefore')}<span className="font-semibold text-white">{t('contact.ctaBold')}</span>{t('contact.ctaAfter')}
                    </p>
                    <div className="mt-10">
                        <a href="https://wa.me/524423424429?text=Hola,%20me%20gustaría%20recibir%20información%20sobre%20sus%20servicios." target="_blank" rel="noreferrer" className="btn btn-accent">
                            <i className="fab fa-whatsapp"></i>
                            {t('contact.ctaButton')}
                        </a>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Contact;
